import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { getDefaultStore, useAtomValue } from "jotai";
import { useState, type JSX } from "react";

import { RecurrenceAvailabilityCalendar } from "@/components/recurring-appointment/recurrence-availability-calendar";
import { RecurrenceCreateHeader } from "@/components/recurring-appointment/recurrence-create-header";
import { Calendar } from "@/components/ui/calendar/calendar";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { recurringAppointmentFormAtom } from "@/lib/atoms/recurring-appointment-form-atom";
import { rescheduleRecurrenceAtom } from "@/lib/atoms/reschedule-recurrence-form-atom";
import { formatIgnoringTimezone, getCurrentDate } from "@/lib/helpers/date";
import { getFrequencyLabel } from "@/lib/helpers/recurrence";

const RescheduleRecurrenceAvailabilityPage = (): JSX.Element => {
  const navigate = useNavigate();
  const router = useRouter();
  const form = useAtomValue(recurringAppointmentFormAtom);
  const { oldSessions } = useAtomValue(rescheduleRecurrenceAtom);

  const [selectedDate, setSelectedDate] = useState(
    formatIgnoringTimezone(form.startDate ?? getCurrentDate()),
  );

  const subtitle = [
    form.unitAcronym,
    form.appointmentTypeName,
    getFrequencyLabel(form.frequency),
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <RoundedLayout>
      <RecurrenceCreateHeader
        title="Disponibilidade"
        subtitle={subtitle}
        onBack={() => router.history.back()}
      />
      <Page>
        <div className="pb-4">
          <Calendar
            initialView="dayGridWeek"
            possibleViews={["dayGridMonth", "dayGridWeek"]}
            selectedDate={selectedDate}
            disabledPastDates
            onDateClick={(date) => setSelectedDate(date)}
          />
        </div>
        <RecurrenceAvailabilityCalendar
          currentDate={selectedDate}
          oldSessionDates={oldSessions.map((session) => session.data)}
          onSlotSelected={() =>
            void navigate({ to: "/recurring-appointments/reschedule/review" })
          }
        />
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/recurring-appointments/reschedule/availability")({
  component: RescheduleRecurrenceAvailabilityPage,
  beforeLoad: () => {
    const form = getDefaultStore().get(recurringAppointmentFormAtom);

    // O padrão precisa estar semeado pela tela de escopo (unidade/tipo/forma).
    if (form.unit === 0 || form.appointmentType === 0 || form.paymentMethod === 0) {
      redirect({ to: "/calendar", throw: true });
    }
  },
});
